import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Tabs, Tab, Paper, CircularProgress,
  Alert, useTheme, useMediaQuery
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Class as ClassIcon,
  Analytics as AnalyticsIcon,
  Assignment as AssignmentIcon,
  Map as MapIcon,
  Assessment as AssessmentIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';
import api from '../../services/api';
import OverviewTab from '../../components/educator/OverviewTab';
import ClassesTab from '../../components/educator/ClassesTab';
import ClassAnalyticsTab from '../../components/educator/ClassAnalyticsTab';
import CurriculumMapTab from '../../components/educator/CurriculumMapTab';
import ImpactReportTab from '../../components/educator/ImpactReportTab';
import ContentReportsTab from '../../components/educator/ContentReportsTab';
import AssignmentsTab from '../../components/educator/AssignmentsTab';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ py: 2 }}>{children}</Box> : null;
}

export default function TeacherDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tab, setTab] = useState(0);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSchool = useCallback(async () => {
    try {
      setLoading(true);

      // 1) Try learner/teacher school link
      try {
        const res = await api.get('/api/school-link/my-school');
        if (res.data.linked && res.data.school) {
          setSchool(res.data.school);
          return;
        }
      } catch (e) {
        // my-school failed or user not linked — continue to admin fallback
      }

      // 2) Try admin schools endpoint (platform admins)
      try {
        const adminRes = await api.get('/api/admin/schools?limit=1');
        if (adminRes.data.data && adminRes.data.data.length > 0) {
          setSchool(adminRes.data.data[0]);
          return;
        }
      } catch (e) {
        // Not admin either — fall through to error
      }

      // 3) Neither worked
      setError('No school found. Please ensure you are linked to a school.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSchool(); }, [fetchSchool]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">{error}</Alert>
      </Container>
    );
  }

  const schoolId = school._id;

  const tabs = [
    { label: 'Overview', icon: <DashboardIcon /> },
    { label: 'Classes', icon: <ClassIcon /> },
    { label: 'Analytics', icon: <AnalyticsIcon /> },
    { label: 'Assignments', icon: <AssignmentIcon /> },
    { label: 'Curriculum', icon: <MapIcon /> },
    { label: 'Impact', icon: <AssessmentIcon /> },
    { label: 'Content Reports', icon: <BugReportIcon /> },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4, px: isMobile ? 1 : 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">
          {school.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {school.province} • {school.district || 'District not set'}
        </Typography>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {tabs.map((t, i) => (
            <Tab
              key={i}
              label={isMobile ? undefined : t.label}
              icon={t.icon}
              iconPosition="start"
              aria-label={t.label}
              sx={{ minHeight: 48, textTransform: 'none', fontSize: '0.875rem' }}
            />
          ))}
        </Tabs>
      </Paper>

      <TabPanel value={tab} index={0}>
        <OverviewTab schoolId={schoolId} />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <ClassesTab schoolId={schoolId} />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <ClassAnalyticsTab schoolId={schoolId} />
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <AssignmentsTab schoolId={schoolId} />
      </TabPanel>
      <TabPanel value={tab} index={4}>
        <CurriculumMapTab schoolId={schoolId} />
      </TabPanel>
      <TabPanel value={tab} index={5}>
        <ImpactReportTab schoolId={schoolId} school={school} />
      </TabPanel>
      <TabPanel value={tab} index={6}>
        <ContentReportsTab schoolId={schoolId} />
      </TabPanel>
    </Container>
  );
}
